import { FC, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, ListGroup, ProgressBar, Row, Spinner, Stack } from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CaresPageHeader from '../shell/cares-page-header';
import DraftWorkflowPanel from './draft-workflow-panel';
import { getCaresShellBootstrap } from '../shell/bootstrap';
import {
  fetchSchedulerDraftReview,
  type SchedulerReviewAssignmentMode,
  type SchedulerReviewConflictItem,
  type SchedulerReviewConflictSeverity,
  type SchedulerReviewResponse,
  type SchedulerReviewStatus,
} from './api';

const severityVariant: Record<SchedulerReviewConflictSeverity, string> = {
  critical: 'danger',
  gap: 'warning',
  warning: 'info',
  review: 'secondary',
};

const statusLabelMap: Record<SchedulerReviewStatus, string> = {
  feasible_with_gaps: 'Feasible with gaps',
  feasible: 'Publish-ready',
  no_feasible_schedule: 'No feasible schedule',
};

const assignmentModeBadgeClass: Record<SchedulerReviewAssignmentMode, string> = {
  solver: 'bg-primary-transparent text-primary',
  template_default: 'bg-info-transparent text-info',
  locked_manual: 'bg-warning-transparent text-warning',
  approved_exception: 'bg-success-transparent text-success',
};

const statusToneClass: Record<'danger' | 'warning' | 'success' | 'info', string> = {
  danger: 'text-danger',
  warning: 'text-warning',
  success: 'text-success',
  info: 'text-info',
};

const DraftReviewPage: FC = () => {
  const bootstrap = getCaresShellBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const [reviewData, setReviewData] = useState<SchedulerReviewResponse | null>(null);
  const [selectedConflictId, setSelectedConflictId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const params = new URLSearchParams(location.search);

    const loadReview = async () => {
      setLoading(true);
      setError(null);

      try {
        const payload = await fetchSchedulerDraftReview({
          group: params.get('group') ?? undefined,
          periodStart: params.get('period_start') ?? undefined,
          periodEnd: params.get('period_end') ?? undefined,
        });

        if (!cancelled) {
          setReviewData(payload);
          const canonicalParams = new URLSearchParams(location.search);
          canonicalParams.set('company', payload.selectedGroup.company.slug);
          canonicalParams.set('group', payload.selectedGroup.slug);
          if (canonicalParams.toString() !== params.toString()) {
            navigate(
              { pathname: location.pathname, search: `?${canonicalParams.toString()}` },
              { replace: true }
            );
          }
        }
      } catch (requestError) {
        if (!cancelled) {
          setReviewData(null);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load scheduler draft review.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadReview();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate]);

  const allConflictItems = useMemo(
    () => reviewData?.review.conflictSections.flatMap((section) => section.items) ?? [],
    [reviewData]
  );

  useEffect(() => {
    if (allConflictItems.length === 0) {
      setSelectedConflictId('');
      return;
    }

    setSelectedConflictId((current) =>
      current && allConflictItems.some((item) => item.id === current) ? current : allConflictItems[0].id
    );
  }, [allConflictItems]);

  const selectedConflict: SchedulerReviewConflictItem | undefined =
    allConflictItems.find((item) => item.id === selectedConflictId) ?? allConflictItems[0];
  const selectedShift = useMemo(
    () =>
      reviewData?.review.scheduleDays
        .flatMap((day) => day.shifts)
        .find((shift) => shift.id === selectedConflict?.shiftId) ??
      reviewData?.review.scheduleDays[0]?.shifts[0],
    [reviewData, selectedConflict]
  );
  const readinessScore = Math.max(
    0,
    100 -
      (reviewData?.review.counters.hardConflicts ?? 0) * 28 -
      (reviewData?.review.counters.unfilledShifts ?? 0) * 18 -
      (reviewData?.review.counters.softWarnings ?? 0) * 4
  );

  if (!bootstrap.authenticated) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Review Schedule Draft" />
        <Alert variant="danger" className="custom-card">
          This scheduler workspace requires an authenticated CARES session.
          <Link to="/login" className="alert-link ms-1">
            Sign in
          </Link>
          to review draft runs.
        </Alert>
      </>
    );
  }

  const selectedGroup = reviewData?.selectedGroup;

  if (loading) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Review Schedule Draft" />
        <Alert variant="light" className="d-flex align-items-center gap-2">
          <Spinner animation="border" size="sm" />
          Loading scheduler draft review contract.
        </Alert>
      </>
    );
  }

  if (error) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Review Schedule Draft" />
        <Alert variant="warning" className="custom-card mb-0">
          {error}
        </Alert>
      </>
    );
  }

  if (!reviewData || !selectedConflict || !selectedShift || !selectedGroup) {
    return (
      <>
        <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Review Schedule Draft" />
        <Alert variant="info" className="custom-card mb-0">
          No scheduler review departments are currently available for this session.
        </Alert>
      </>
    );
  }

  const handleGroupChange = (nextGroupId: string) => {
    const params = new URLSearchParams(location.search);
    params.set('group', nextGroupId);
    navigate({
      pathname: location.pathname,
      search: `?${params.toString()}`,
    });
  };

  return (
    <>
      <CaresPageHeader homepage="Scheduling" activepage="Scheduling" page="Review Schedule Draft" />

      <Card className="custom-card cares-page-hero">
        <Card.Body className="p-4 p-xl-5">
          <Row className="g-4 align-items-start">
            <Col xl={8}>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <Badge bg="" className="bg-primary-transparent text-primary">
                  {statusLabelMap[reviewData.review.resultStatus]}
                </Badge>
                <Badge bg="" className="bg-light text-muted border">
                  {reviewData.review.draftRevisionId}
                </Badge>
                <Badge bg="" className="bg-light text-muted border">
                  {reviewData.review.runId}
                </Badge>
                <Badge bg="" className="bg-light text-muted border">
                  {selectedGroup.company.name}
                </Badge>
              </div>
              <h2 className="mb-2">{reviewData.review.groupName}</h2>
              <p className="text-muted mb-3">
                {reviewData.review.schedulePeriod} • Generated {reviewData.review.generatedAt}
              </p>
              <p className="mb-4 fs-15" style={{ maxWidth: '58rem' }}>
                Open the draft, triage blockers first, and inspect why each assignment won or failed before direct editing exists.
              </p>
              <div className="mb-4" style={{ maxWidth: '24rem' }}>
                <Form.Label className="text-uppercase text-muted fs-12 fw-semibold mb-2">Scheduler department</Form.Label>
                <Form.Select
                  value={selectedGroup.slug}
                  onChange={(event) => handleGroupChange(event.target.value)}
                >
                  {reviewData.availableGroups.map((group) => (
                    <option key={group.id} value={group.slug}>
                      {group.name} ({group.company.slug})
                    </option>
                  ))}
                </Form.Select>
              </div>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <Button variant="primary" disabled>
                  Review blockers
                </Button>
                <Button variant="light" disabled>
                  Save notes
                </Button>
                <Button variant="outline-primary" disabled>
                  Rerun draft
                </Button>
                <Button variant="success" disabled>
                  Publish
                </Button>
              </Stack>
            </Col>
            <Col xl={4}>
              <Card className="custom-card shadow-none border mb-0 bg-white bg-opacity-75">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <p className="text-uppercase text-muted fs-12 fw-semibold mb-1">Draft readiness</p>
                      <h3 className="mb-0">{readinessScore}%</h3>
                    </div>
                    <Badge bg="" className="bg-warning-transparent text-warning">
                      Blocked
                    </Badge>
                  </div>
                  <ProgressBar now={readinessScore} variant={readinessScore >= 80 ? 'success' : 'warning'} className="progress-xl mb-3" />
                  <p className="text-muted fs-13 mb-3">{reviewData.review.publishReason}</p>
                  <div className="border rounded-3 p-3 bg-light">
                    <p className="fw-semibold mb-1">Rerun preservation</p>
                    <p className="text-muted fs-13 mb-0">{reviewData.review.preservationMode}</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Row className="g-4 mt-0">
        {[
          ['Unfilled shifts', `${reviewData.review.counters.unfilledShifts}`, 'danger'],
          ['Hard conflicts', `${reviewData.review.counters.hardConflicts}`, 'warning'],
          ['Soft warnings', `${reviewData.review.counters.softWarnings}`, 'info'],
          ['Locked assignments', `${reviewData.review.counters.lockedAssignments}`, 'success'],
          ['Manual edits preserved', `${reviewData.review.counters.manualEdits}`, 'primary'],
        ].map(([label, value, tone]) => (
          <Col xl={true} md={6} key={label}>
            <Card className="custom-card h-100">
              <Card.Body>
                <p className="text-uppercase text-muted fs-12 mb-2">{label}</p>
                <div className="d-flex align-items-center justify-content-between">
                  <h3 className="mb-0">{value}</h3>
                  <span className={`avatar avatar-md bg-${tone}-transparent text-${tone}`}>
                    <i className="ri-pulse-line fs-18"></i>
                  </span>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      <Alert variant="info" className="d-flex align-items-start gap-2">
        <i className="ri-information-line fs-18 mt-1"></i>
        <div>
          <div className="fw-semibold">Read-only first slice</div>
          <div>{reviewData.review.dataNotice}</div>
          <div className="text-muted fs-12 mt-1">
            Source: {reviewData.review.dataSource} • Period {reviewData.meta.periodStart} to {reviewData.meta.periodEnd}
          </div>
        </div>
      </Alert>

      <DraftWorkflowPanel company={selectedGroup.company.slug} group={selectedGroup.slug} />

      <Row className="g-4">
        <Col xxl={3}>
          <Card className="custom-card h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="card-title mb-0">Conflict Inbox</div>
              <Badge bg="" className="bg-light text-muted border">
                {allConflictItems.length} items
              </Badge>
            </Card.Header>
            <Card.Body className="p-0">
                {reviewData.review.conflictSections.map((section) => (
                <div key={section.title} className="border-bottom">
                  <div className="px-3 pt-3 pb-2">
                    <p className="text-uppercase text-muted fs-12 fw-semibold mb-0">{section.title}</p>
                  </div>
                  <ListGroup variant="flush">
                    {section.items.map((item) => {
                      const active = selectedConflict?.id === item.id;
                      return (
                        <ListGroup.Item
                          action
                          key={item.id}
                          active={active}
                          onClick={() => setSelectedConflictId(item.id)}
                          className="border-0 border-top rounded-0"
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                <Badge bg={active ? 'light' : severityVariant[item.severity]} text={active ? 'dark' : undefined}>
                                  {item.severity}
                                </Badge>
                                <span className="fs-12 opacity-75">{item.unitLabel}</span>
                              </div>
                              <div className="fw-semibold">{item.title}</div>
                              <div className="fs-13 opacity-75 mt-1">{item.rootCause}</div>
                            </div>
                            <i className="ri-arrow-right-s-line fs-18 opacity-75"></i>
                          </div>
                          <div className="fs-13 mt-2 opacity-75">{item.summary}</div>
                          <div className="fs-12 mt-2 text-uppercase opacity-75">
                            Recommended path: {item.resolutionPath}
                          </div>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>

        <Col xxl={5}>
          <Card className="custom-card h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="card-title mb-0">Schedule Review Canvas</div>
              <Badge bg="" className="bg-light text-muted border">
                Read-only
              </Badge>
            </Card.Header>
            <Card.Body>
              <Stack gap={4}>
                {reviewData.review.scheduleDays.map((day) => (
                  <div key={day.dayLabel}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div>
                        <h5 className="mb-0">{day.dayLabel}</h5>
                        <p className="text-muted fs-13 mb-0">Date-grouped review lanes keep blockers and assignments in one pass.</p>
                      </div>
                    </div>
                    <Row className="g-3">
                      {day.shifts.map((shift) => {
                        const active = shift.id === selectedShift?.id;
                        const linkedConflictId = shift.issueIds[0];
                        return (
                          <Col md={6} key={shift.id}>
                            <Card
                              className={`custom-card h-100 border ${active ? 'border-primary shadow-sm' : ''}`}
                              role="button"
                              onClick={() => linkedConflictId && setSelectedConflictId(linkedConflictId)}
                            >
                              <Card.Body>
                                <div className="d-flex align-items-start justify-content-between gap-2 mb-3">
                                  <div>
                                    <p className="text-uppercase text-muted fs-12 mb-1">{shift.laneLabel}</p>
                                    <h6 className="mb-1">{shift.assignmentLabel}</h6>
                                    <p className="text-muted fs-13 mb-0">{shift.timeLabel}</p>
                                  </div>
                                  <div className="text-end">
                                    <Badge bg="" className={assignmentModeBadgeClass[shift.assignmentMode]}>
                                      {shift.assignmentMode.replace('_', ' ')}
                                    </Badge>
                                    <div className={`fs-13 fw-semibold mt-2 ${statusToneClass[shift.statusTone]}`}>
                                      {shift.issueCount === 0 ? 'Healthy' : `${shift.issueCount} review item${shift.issueCount > 1 ? 's' : ''}`}
                                    </div>
                                  </div>
                                </div>
                                <p className="text-muted fs-13 mb-3">{shift.summary}</p>
                                <div className="d-flex align-items-center justify-content-between">
                                  <span className="fs-12 text-uppercase text-muted">Next review surface</span>
                                  <i className="ri-focus-3-line fs-18 text-primary"></i>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </div>
                ))}
              </Stack>
            </Card.Body>
          </Card>
        </Col>

        <Col xxl={4}>
          <Card className="custom-card h-100">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="card-title mb-0">Explainability Drawer</div>
              <Badge bg="" className={`bg-${severityVariant[selectedConflict.severity]}-transparent text-${severityVariant[selectedConflict.severity]}`}>
                {selectedConflict.resolutionPath}
              </Badge>
            </Card.Header>
            <Card.Body>
              <Stack gap={3}>
                <div className="border rounded-3 p-3 bg-light">
                  <p className="text-uppercase text-muted fs-12 mb-1">Assignment summary</p>
                  <h5 className="mb-1">{selectedConflict.title}</h5>
                  <p className="text-muted mb-2">{selectedConflict.summary}</p>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge bg="" className="bg-light text-default border">
                      {selectedConflict.unitLabel}
                    </Badge>
                    <Badge bg="" className="bg-light text-default border">
                      Root cause: {selectedConflict.rootCause}
                    </Badge>
                  </div>
                </div>

                <div>
                  <p className="text-uppercase text-muted fs-12 mb-2">Why this assignment won</p>
                  <ListGroup variant="flush">
                    {selectedShift?.whyThisAssignmentWon.map((item) => (
                      <ListGroup.Item key={item} className="px-0">
                        {item}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>

                <div>
                  <p className="text-uppercase text-muted fs-12 mb-2">Why other candidates failed</p>
                  <ListGroup variant="flush">
                    {selectedShift?.whyOthersFailed.map((item) => (
                      <ListGroup.Item key={item} className="px-0">
                        {item}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>

                <div>
                  <p className="text-uppercase text-muted fs-12 mb-2">What can I do next</p>
                  <ListGroup variant="flush">
                    {selectedShift?.nextSteps.map((item) => (
                      <ListGroup.Item key={item} className="px-0">
                        {item}
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </div>

                {selectedConflict.notes?.length ? (
                  <div className="border rounded-3 p-3">
                    <p className="text-uppercase text-muted fs-12 mb-2">Review notes</p>
                    <ListGroup variant="flush">
                      {selectedConflict.notes.map((note) => (
                        <ListGroup.Item key={note} className="px-0">
                          {note}
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  </div>
                ) : null}

                {selectedConflict.blockerText ? (
                  <Alert variant="danger" className="mb-0">
                    {selectedConflict.blockerText}
                  </Alert>
                ) : null}

                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-primary" disabled>
                    Reassign within eligible pool
                  </Button>
                  <Button variant="outline-secondary" disabled>
                    Mark for rerun
                  </Button>
                  <Button variant="outline-danger" disabled={selectedConflict.resolutionPath !== 'Exception request'}>
                    Request exception
                  </Button>
                </div>

                <p className="text-muted fs-13 mb-0">
                  Draft notes, rerun confirmation, and exception entry points are intentionally visible but disabled in this shell.
                </p>
              </Stack>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default DraftReviewPage;
